import { useState } from "react";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Box,
  Typography,
  TextField,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import ConfirmButton from "../fundamentals/ConfirmButton";
import { getFormattedPhoneNumber } from "../../util/commonUtils";
import { validatePhoneNumber } from "../../util/inputValidation";
import { INVALID_PHONE_NUMBER, PHONE_REQUIRED } from "../../util/messageConstants";
import ScheduledHoursSegment from "./ScheduledHoursSegment";
import {
  CONTINUOUS_WITH_PREFERENCES_TWU,
  OT_ELIGIBILITY_EMPLOYEE_WARNING,
  OT_STATUS_IS_REQUIRED_ERROR,
  OT_STATUS_LIST,
} from "../../util/overtime/constants";
import { CONTRACT_SENIORITY_LIST_TYPE_MAPPER, TWU_CONTRACT } from "../../util/constants";
import {
  validateCalloutSectionDuplication,
  validateContinuousWithOrCalloutPreferenceSelected,
  validateShiftSegments,
} from "../../util/overtime/inputValidation";
import { getLatestPubSLEvent } from "../../api/seniority";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { useSelector } from "react-redux";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import { buildVoluntaryOTSignupPayload, getCalloutOTAssignmentPreferenceList } from "../../util/overtime/overtimeUtils";
import { addOvertimeEvent, isOvertimeEventEligible } from "../../api/overtime";
import SuccessDialog from "../fundamentals/SuccessDialog";

const useStyles = makeStyles((theme) => ({
  button: {
    width: "190px",
    float: "right",
    fontWeight: "bold",
  },
  mobileButton: {
    width: "100%",
    fontWeight: "bold",
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 0,
    paddingRight: 0,
    borderRadius: 5,
  },
  sectionTitleFontStyle: {
    fontSize: "16px",
    fontWeight: "bold",
    float: "left",
    marginTop: "10px",
    marginRight: "10px",
    color: "black",
  },
  sectionTitleDescriptionFontStyle: { fontStyle: "italic", fontSize: "14px", marginTop: "12px" },
  sectionSubtitleFontStyle: {
    fontSize: "14px",
    fontWeight: "bold",
    float: "left",
    marginTop: "10px",
    marginRight: "10px",
    color: theme.palette.secondary.swaBlue,
  },
  listFontStyle: {
    fontSize: "14px",
    float: "left",
    marginTop: "10px",
    marginRight: "10px",
    color: theme.palette.secondary.swaBlue,
  },
  sectionBox: {
    display: "inline-block",
    width: "100%",
  },
}));

const CreateVoluntaryOTSignupTWU = (props) => {
  //#region Constants and State
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isSignupDialogOpen, setIsSignupDialogOpen] = useState(false);
  const userContract = TWU_CONTRACT;
  //Seniority Date section
  const [seniorityDate, setSeniorityDate] = useState("");
  const [isSeniorityDateLoading, setIsSeniorityDateLoading] = useState(false);
  //Phone Number section
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberErrors, setPhoneNumberErrors] = useState("");
  //Scheduled Hours section
  const [shiftOffChecked, setShiftOffChecked] = useState(false);
  const shiftObjectInitialState = {
    shiftStartHr: "",
    shiftStartMin: "",
    shiftEndHr: "",
    shiftEndMin: "",
    shiftSegmentError: "",
  };
  const [shifts, setShifts] = useState([shiftObjectInitialState]);
  //OT Status section
  const [otStatus, setOtStatus] = useState("");
  const [otStatusError, setOtStatusError] = useState("");
  //Callout OT Preferences section
  const calloutAssignmentsInitialState = getCalloutOTAssignmentPreferenceList(props.workgroup).map(() => {
    return { preference: "", error: "" };
  });
  const [calloutAssignments, setCalloutAssignments] = useState(calloutAssignmentsInitialState);
  //Continuous With Preferences section
  const [continuousWith, setContinuousWith] = useState("");
  const [continuousWithError, setContinuousWithError] = useState("");
  //Bottom Checkboxes section
  const [willAcceptChecked, setWillAcceptChecked] = useState(false);
  const [waive45MinChecked, setWaive45MinChecked] = useState(false);
  //Confirmation Dialogs section
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSubmitConfirmationDialogOpen, setIsSubmitConfirmationDialogOpen] = useState(false);
  const [isSubmitSuccessDialogOpen, setIsSubmitSuccessDialogOpen] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  //#endregion

  //#region API Functions
  const getSeniorityDate = async () => {
    try {
      setIsSeniorityDateLoading(true);
      const seniorityListTypes = CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userContract, props.station);
      for (const seniorityListType of seniorityListTypes) {
        const response = await getLatestPubSLEvent(props.station, props.workgroup, seniorityListType);
        const data = await response.json();
        const empSlRecord = data?.seniorityList?.find((item) => item?.eid === userSession.eid);
        if (empSlRecord) {
          setSeniorityDate(empSlRecord.seniorityDate);
        } else {
          setSeniorityDate("N/A");
        }
      }
    } catch (e) {
      console.error("Error fetching seniority date:", e);
    } finally {
      setIsSeniorityDateLoading(false);
    }
  };

  const isOTEventEligibleForSignup = async () => {
    let isEligible = false;
    let error = "";
    try {
      setIsSubmitLoading(true);
      const result = await isOvertimeEventEligible(userSession.eid, props.station, props.selectedDate.toISO());
      isEligible = result.status === 200;

      if (result.status === 400) {
        error = OT_ELIGIBILITY_EMPLOYEE_WARNING;
      } else if (result.status === 500) {
        error = "Failed to process request. Please try again later.";
      }
    } catch (e) {
      console.error("Error fetching event eligibility:", e);
      error = "Failed to process request. Please try again later.";
      isEligible = false;
    } finally {
      setSubmitErrorMessage(error);
      setIsSubmitLoading(false);
      setIsSubmitSuccessDialogOpen(!isEligible);
    }
    return isEligible;
  };

  const addOvertimeSignupEvent = async (payload) => {
    let error = "";
    try {
      setIsSubmitLoading(true);
      const response = await addOvertimeEvent(payload);
      if (response.status !== 200) {
        error = "Failed to submit Voluntary OT Signup. Please try again later.";
      }
    } catch (e) {
      console.error("Error submitting signup:", e);
      error = "Failed to submit Voluntary OT Signup. Please try again later.";
    } finally {
      setSubmitErrorMessage(error);
      setIsSubmitLoading(false);
      setIsSubmitSuccessDialogOpen(true);
    }
  };
  //#endregion

  //#region Validation Functions
  const validateContactNumber = () => {
    let isFormValid = false;
    const phoneNumberValidation = validatePhoneNumber(phoneNumber, PHONE_REQUIRED, INVALID_PHONE_NUMBER);
    setPhoneNumberErrors(phoneNumberValidation);
    if (phoneNumberValidation === "" && phoneNumber !== "") {
      const formattedPhoneNumber = getFormattedPhoneNumber(phoneNumber);
      setPhoneNumber(formattedPhoneNumber);
      isFormValid = true;
    }
    return isFormValid;
  };

  const validateOTStatus = () => {
    let isOTStatusFormValid = true;
    if (otStatus === "") {
      setOtStatusError(OT_STATUS_IS_REQUIRED_ERROR);
      isOTStatusFormValid = false;
    } else {
      setOtStatusError("");
    }
    return isOTStatusFormValid;
  };

  const validateContinuousWithAndCalloutSections = () => {
    //Continuous With Preferences and Callout OT Preferences section (either can be optional, but at least one must be selected)
    let isContinuousWithAndCalloutPreferencesValid = true;
    const continuousWithError = validateContinuousWithOrCalloutPreferenceSelected(
      continuousWith,
      calloutAssignments,
      []
    );

    if (continuousWithError !== "") {
      setContinuousWithError(continuousWithError);
      updateCalloutPreferencesErrors(calloutAssignments, continuousWithError, setCalloutAssignments);
      isContinuousWithAndCalloutPreferencesValid = false;
    } else if (calloutAssignments.some((pref) => pref.error !== "")) {
      //section had errors already
      isContinuousWithAndCalloutPreferencesValid = false;
    } else {
      //clear errors
      setContinuousWithError("");
      updateCalloutPreferencesErrors(calloutAssignments, "", setCalloutAssignments);
    }

    if (isContinuousWithAndCalloutPreferencesValid) {
      //Validate duplication to ensure no duplicates exist on submit
      let updatedCalloutAssignments = [...calloutAssignments];
      updatedCalloutAssignments = validateCalloutSectionDuplication(updatedCalloutAssignments);
      setCalloutAssignments(updatedCalloutAssignments);

      if (updatedCalloutAssignments.some((pref) => pref.error !== "")) {
        isContinuousWithAndCalloutPreferencesValid = false;
      }
    }

    return isContinuousWithAndCalloutPreferencesValid;
  };

  const validateSignupForm = () => {
    //Phone Number section
    const isPhoneFormValid = validateContactNumber();

    //Scheduled Hours section
    let isShiftFormValid = true;
    if (!shiftOffChecked) {
      const { segments, isFormValid } = validateShiftSegments(shifts, props.selectedDate, props.station, userContract);
      isShiftFormValid = isFormValid;
      setShifts(segments);
    }

    //OT Status section
    const isOTStatusFormValid = validateOTStatus();

    //Continuous With Preferences and Callout OT Preferences section
    const isContinuousWithAndCalloutPreferencesValid = validateContinuousWithAndCalloutSections();

    return isPhoneFormValid && isShiftFormValid && isOTStatusFormValid && isContinuousWithAndCalloutPreferencesValid;
  };

  const resetForm = () => {
    //Phone Number section
    setPhoneNumber("");
    setPhoneNumberErrors("");

    //Scheduled Hours section
    setShiftOffChecked(false);
    setShifts([shiftObjectInitialState]);

    //OT Status section
    setOtStatus("");
    setOtStatusError("");

    //Continuous With and Callout OT Preferences sections
    resetPreferencesSections();

    //Bottom Checkboxes section
    setWillAcceptChecked(false);
    setWaive45MinChecked(false);

    //Dialogs section
    setSubmitErrorMessage("");
  };

  const resetPreferencesSections = () => {
    //Continuous With Preferences section
    setContinuousWith("");
    setContinuousWithError("");

    //Callout OT Preferences section
    setCalloutAssignments(calloutAssignmentsInitialState);
  };

  const updateCalloutPreferencesErrors = (preferences, errorMessage, preferencesSetterFunc) => {
    const copiedPreferences = [...preferences];
    copiedPreferences.forEach((preference) => {
      if (preference.error === "" || errorMessage === "") preference.error = errorMessage;
    });
    preferencesSetterFunc(copiedPreferences);
  };
  //#endregion

  //#region Dialog Handlers
  const handleClickDialogOpen = async () => {
    setIsSignupDialogOpen(true);
    await getSeniorityDate();
  };

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSignupDialogOpen(false);
    resetForm();
  };
  //#endregion

  //#region Phone section handlers
  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  const handlePhoneNumberBlur = () => {
    validateContactNumber();
  };
  //#endregion

  //#region Scheduled Hours section handlers
  const handleScheduledHoursOffCheckedChange = (event) => {
    setShiftOffChecked(event.target.checked);
    if (event.target.checked) {
      setShifts([shiftObjectInitialState]);
    }
  };

  const handleAddShiftSegment = () => {
    const existingSegments = [...shifts];
    setShifts([...existingSegments, shiftObjectInitialState]);
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${shifts.length + 1}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 0);
  };
  //#endregion

  //#region OT Status Handlers
  const handleOtStatusPreferenceChange = (event) => {
    setOtStatus(event.target.value);
    setOtStatusError("");
  };
  //#endregion

  //region Callout OT Preferences Handlers
  const handleCalloutAssignmentPreferenceChange = (event, index) => {
    // Clear any existing errors
    let updatedPreferences = [...calloutAssignments];
    setContinuousWithError("");

    updatedPreferences[index].preference = event.target.value;
    updatedPreferences = validateCalloutSectionDuplication(updatedPreferences);
    setCalloutAssignments(updatedPreferences);
  };
  //#endregion

  //#region Continuous With Preferences Handlers
  const handleContinuousWithPreferenceChange = (event) => {
    setContinuousWith(event.target.value);
    setContinuousWithError("");
    updateCalloutPreferencesErrors(calloutAssignments, "", setCalloutAssignments);
  };
  //#endregion

  const handleOnSubmitClick = async () => {
    const isFormValid = validateSignupForm();

    if (isFormValid) {
      const isEligible = await isOTEventEligibleForSignup();
      setIsSubmitConfirmationDialogOpen(isEligible);
    }
  };

  const handleSubmitYesClicked = async () => {
    const signupData = {
      date: props.selectedDate,
      station: props.station,
      workgroup: props.workgroup,
      phoneNumber: phoneNumber,
      isShiftOff: shiftOffChecked,
      shifts: shifts,
      otStatus: otStatus,
      continuousWith: continuousWith,
      calloutAssignments: calloutAssignments,
      willAccept: willAcceptChecked,
      waive45Min: waive45MinChecked,
    };

    const payload = buildVoluntaryOTSignupPayload(userSession, userContract, signupData);
    await addOvertimeSignupEvent(payload);
  };

  //#region Component Renders
  const renderPhoneNumberSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Box className={classes.sectionBox}>
            <Typography className={classes.sectionTitleFontStyle}>Phone #</Typography>
            <TextField
              value={phoneNumber}
              variant="outlined"
              label="Enter Phone #"
              onChange={handlePhoneNumberChange}
              onBlur={handlePhoneNumberBlur}
              error={phoneNumberErrors !== ""}
              size="small"
              helperText={phoneNumberErrors}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderScheduledHoursSection = () => {
    return (
      <Grid container>
        <Grid item xs={10}>
          <Typography className={classes.sectionTitleFontStyle}>Scheduled Hours</Typography>
        </Grid>
        <Grid item xs={2}>
          <Box sx={{ position: "relative" }}>
            <FormControl variant="outlined">
              <FormControlLabel
                data-testid="shiftOffCheckbox"
                checked={shiftOffChecked}
                onChange={handleScheduledHoursOffCheckedChange}
                control={<Checkbox />}
                label="OFF"
              />
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <ScheduledHoursSegment
            shiftSegments={shifts}
            scheduledHoursOffChecked={shiftOffChecked}
            setShiftSegments={setShifts}
          />
        </Grid>
        <Grid container justifyContent="center">
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              className={classes.button}
              variant="contained"
              onClick={handleAddShiftSegment}
              disabled={shifts.length >= 10 || shiftOffChecked}
            >
              Add Another Shift
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderOTStatusPreferences = () => {
    return OT_STATUS_LIST.map((preference) => {
      return (
        <MenuItem key={preference} value={preference}>
          {preference}
        </MenuItem>
      );
    });
  };

  const renderOTStatusSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.sectionTitleFontStyle}>OT Status</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.sectionBox} mt={1} mb={1}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="otStatusLabel" sx={{ marginTop: otStatus ? "0px" : "-8px" }} error={!!otStatusError}>
                OT Status
              </InputLabel>
              <Select
                size="small"
                labelId="otStatusLabel"
                id="otStatusSelectElement"
                value={otStatus}
                onChange={handleOtStatusPreferenceChange}
                label="OT Status"
                error={!!otStatusError}
              >
                <MenuItem value="" disabled>
                  <em>OT Status</em>
                </MenuItem>
                {renderOTStatusPreferences()}
              </Select>
              <FormHelperText error>{otStatusError}</FormHelperText>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderContinuousWithPreferences = () => {
    return CONTINUOUS_WITH_PREFERENCES_TWU.map((preference) => {
      return (
        <MenuItem key={preference} value={preference}>
          {preference}
        </MenuItem>
      );
    });
  };

  const renderContinuousWithPreferencesSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.sectionTitleFontStyle}>Continuous With Preferences</Typography>{" "}
          <Typography className={classes.sectionTitleDescriptionFontStyle}>- ext. less than 4 hours</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.sectionBox} mt={1} mb={1}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                id="continuousWithLabel"
                sx={{ marginTop: continuousWith ? "0px" : "-8px" }}
                error={!!continuousWithError}
              >
                Continuous With
              </InputLabel>
              <Select
                size="small"
                labelId="continuousWithLabel"
                id="continuousWithSelectElement"
                value={continuousWith}
                onChange={handleContinuousWithPreferenceChange}
                label="Continuous With"
                error={!!continuousWithError}
              >
                <MenuItem value="" disabled>
                  <em>Continuous With</em>
                </MenuItem>
                {renderContinuousWithPreferences()}
              </Select>
              <FormHelperText error>{continuousWithError}</FormHelperText>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderCalloutOTAssignmentPreferences = () => {
    return getCalloutOTAssignmentPreferenceList(props.workgroup).map((preference) => {
      return (
        <MenuItem key={preference} value={preference}>
          {preference}
        </MenuItem>
      );
    });
  };

  const renderCalloutOTPreferencesSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.sectionTitleFontStyle}>Callout OT Preferences</Typography>
          <Typography className={classes.sectionTitleDescriptionFontStyle}>- at least 4 hours</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography className={classes.sectionSubtitleFontStyle}>Rank your assignment preferences:</Typography>
        </Grid>
        {getCalloutOTAssignmentPreferenceList(props.workgroup).map((preference, index) => {
          if (index === 0 || calloutAssignments[index - 1].preference !== "") {
            return (
              <Grid container key={`calloutHourPreference-${index + 1}`}>
                <Grid item xs={1}>
                  <Box className={classes.sectionBox} mt={1} mb={1}>
                    <Typography className={classes.listFontStyle}>{index + 1}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={5}>
                  <Box className={classes.sectionBox} mt={1} mb={1}>
                    <FormControl variant="outlined">
                      <InputLabel
                        id={`calloutHourPreference-${index + 1}Label`}
                        sx={{ marginTop: calloutAssignments[index].preference ? "0px" : "-8px" }}
                        error={!!calloutAssignments[index].error}
                      >
                        Preference
                      </InputLabel>
                      <Select
                        size="small"
                        sx={{ width: "160px" }}
                        labelId={`calloutHourPreference-${index + 1}Label`}
                        id={`calloutHourPreference-${index + 1}SelectElement`}
                        value={calloutAssignments[index].preference}
                        onChange={(event) => handleCalloutAssignmentPreferenceChange(event, index)}
                        label="Preference"
                        error={!!calloutAssignments[index].error}
                      >
                        <MenuItem value="" disabled>
                          <em>Preference</em>
                        </MenuItem>
                        {renderCalloutOTAssignmentPreferences()}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                <FormHelperText error>{calloutAssignments[index].error}</FormHelperText>
              </Grid>
            );
          }
          return <></>;
        })}
      </Grid>
    );
  };

  return (
    <div data-testid="create-voluntary-ot-signup">
      <Button
        className={props.isDesktopView ? classes.button : classes.mobileButton}
        variant="contained"
        color="primary"
        onClick={handleClickDialogOpen}
        data-testid="create-new-signup-button"
      >
        Create New Signup
      </Button>

      {isSignupDialogOpen && (
        <Dialog
          className={props.isDesktopView ? "action-check-dialog" : "detail-dialog"}
          data-testid="action-check-dialog"
          fullScreen={!props.isDesktopView}
          fullWidth
          sx={{ flexDirection: "column", maxHeight: "100vh" }}
          maxWidth="md"
          open={isSignupDialogOpen}
          TransitionComponent={SlideUpTransition}
          keepMounted
          onClose={handleClickDialogClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
            {props.station} | {props.workgroup} | {props.selectedDate.toFormat("MM/dd/yyyy")} OT Signup
          </DialogTitle>
          <Box
            sx={{
              bgcolor: "secondary.swaGray2",
              width: "100%",
              minHeight: "50px",
              maxHeight: "50px",
              borderColor: "secondary.swaGray2",
              borderRadius: 0,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={8} sm={8}>
                <Typography sx={{ marginLeft: "10px" }} className={classes.sectionTitleFontStyle}>
                  Seniority Date: {isSeniorityDateLoading ? <CircularProgress size={20} /> : seniorityDate}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={4}>
                <Button
                  style={{ backgroundColor: "white", paddingLeft: 8, paddingRight: 8 }}
                  size="small"
                  sx={{ float: "right", marginTop: "10px", marginRight: "10px" }}
                  onClick={resetForm}
                  variant="outlined"
                >
                  CLEAR ALL
                </Button>
              </Grid>
            </Grid>
          </Box>
          <DialogContent>
            <Grid container sx={{ flexDirection: "column" }}>
              <DialogContentText component={"div"} id="alert-dialog-slide-description">
                {renderPhoneNumberSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderScheduledHoursSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderOTStatusSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderCalloutOTPreferencesSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderContinuousWithPreferencesSection()}
                <Grid container justifyContent="center">
                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                    <ConfirmButton variant="outlined" color="primary" onClick={resetPreferencesSections}>
                      Clear Preferences
                    </ConfirmButton>
                  </Grid>
                </Grid>
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
              </DialogContentText>
            </Grid>
          </DialogContent>
          <DialogActions className="action-check-dialog-buttons">
            <Grid container justifyContent="space-evenly" alignItems="center">
              <Grid item xs={4}>
                <FormControl variant="outlined">
                  <FormControlLabel
                    checked={willAcceptChecked}
                    onChange={(event) => {
                      setWillAcceptChecked(event.target.checked);
                    }}
                    control={<Checkbox />}
                    label="Will Accept"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={5}>
                <FormControl variant="outlined">
                  <FormControlLabel
                    checked={waive45MinChecked}
                    onChange={(event) => {
                      setWaive45MinChecked(event.target.checked);
                    }}
                    control={<Checkbox />}
                    label="Waive 45-Minute Minimum"
                  />
                </FormControl>
              </Grid>

              <Grid container mt={1} mb={1} justifyContent="flex-end" spacing={2}>
                <ConfirmButton variant="outlined" color="primary" onClick={handleClickDialogClose}>
                  Cancel
                </ConfirmButton>
                <div data-testid="submit-signup-button">
                  <ConfirmButton
                    variant="contained"
                    color="primary"
                    onClick={handleOnSubmitClick}
                    isLoading={isSubmitLoading}
                  >
                    Submit
                  </ConfirmButton>
                </div>
              </Grid>
            </Grid>
          </DialogActions>

          <ConfirmationDialog
            isDialogOpen={isSubmitConfirmationDialogOpen}
            message={"Are you sure you want to submit this Signup?"}
            handleNoButtonClicked={() => setIsSubmitConfirmationDialogOpen(false)}
            handleYesButtonClicked={handleSubmitYesClicked}
            isYesLoading={isSubmitLoading}
          />

          <SuccessDialog
            title="Submit"
            isSuccessDialogOpen={isSubmitSuccessDialogOpen}
            message={"Successfully submitted your Voluntary OT Signup"}
            hasError={submitErrorMessage !== ""}
            error={submitErrorMessage}
            handleOKClick={() => {
              setIsSubmitSuccessDialogOpen(false);
              setIsSubmitConfirmationDialogOpen(false);
              if (submitErrorMessage === "") {
                handleClickDialogClose();
              }
            }}
          />
        </Dialog>
      )}
    </div>
  );
  //#endregion
};

CreateVoluntaryOTSignupTWU.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  station: PropTypes.string.isRequired,
  workgroup: PropTypes.string.isRequired,
  selectedDate: PropTypes.object.isRequired,
};

export default CreateVoluntaryOTSignupTWU;
